import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

import { getMediaUrl } from '../../helpers';

/**
 * MatchCard — the shared card design used everywhere we render a single
 * match on the player surfaces. Same visual rhythm:
 *
 *   ┌─────────────────────────────────────┐
 *   │ [logo] Home   SCORE   Away [logo]   │
 *   │ ─────────                           │
 *   │ DATE · MINS PLAYED · ...meta        │
 *   └─────────────────────────────────────┘
 *
 * Props:
 * - homeTeam, awayTeam: strings.
 * - homeTeamLogo, awayTeamLogo: URLs (already absolute or rewritable by
 *   getMediaUrl). Falls back to a circular placeholder if missing.
 * - score: string like "2:1". Falls back to "—" when null.
 * - scoreColor: optional CSS color for the score (W/L/D).
 * - meta: optional array of nodes/strings shown in the meta row below
 *   the team line, joined by middots. Pass an empty array or omit to hide
 *   the row entirely.
 * - to: if present, wraps the card in a react-router <Link>.
 *
 * Competition / round labels are rendered by the caller above the card,
 * not inside — different surfaces want different label rules.
 */
const MatchCard = ({
  homeTeam,
  homeTeamLogo,
  awayTeam,
  awayTeamLogo,
  score,
  scoreColor,
  meta,
  to,
}) => {
  const Container = to ? CardLink : CardDiv;

  const renderedMeta = Array.isArray(meta) ? meta.filter(Boolean) : [];

  return (
    <Container to={to}>
      <TeamRow>
        <Side>
          {homeTeamLogo
            ? <TeamLogo src={getMediaUrl(homeTeamLogo)} alt="" />
            : <TeamLogoPlaceholder />}
          <TeamName>{homeTeam}</TeamName>
        </Side>

        <ScoreValue style={scoreColor ? { color: scoreColor } : undefined}>
          {score ?? '—'}
        </ScoreValue>

        <Side $align="right">
          <TeamName $align="right">{awayTeam}</TeamName>
          {awayTeamLogo
            ? <TeamLogo src={getMediaUrl(awayTeamLogo)} alt="" />
            : <TeamLogoPlaceholder />}
        </Side>
      </TeamRow>

      {renderedMeta.length > 0 && (
        <MetaRow>
          {renderedMeta.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <MetaSeparator>·</MetaSeparator>}
              <MetaItem>{item}</MetaItem>
            </React.Fragment>
          ))}
        </MetaRow>
      )}
    </Container>
  );
};

export default MatchCard;

const baseCardCss = `
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
`;

const CardDiv = styled.div`
  ${baseCardCss}
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.white};
`;

const CardLink = styled(Link)`
  ${baseCardCss}
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.white};
  text-decoration: none;
  transition: border-color 120ms ease, transform 80ms ease;

  &:hover {
    border-color: ${(props) => props.theme.blueish2};
  }

  &:active {
    transform: scale(0.995);
  }
`;

// color: inherit so the global "* { color: theme.green }" rule doesn't
// repaint these intermediate containers and break the chain from
// CardDiv (white) down to TeamName / ScoreValue.
const TeamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: inherit;
`;

const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  justify-content: ${(props) => (props.$align === 'right' ? 'flex-end' : 'flex-start')};
  color: inherit;
`;

const TeamLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
`;

const TeamLogoPlaceholder = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${(props) => props.theme.borderColor};
  flex-shrink: 0;
`;

const TeamName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  color: ${(props) => props.theme.white};
  text-align: ${(props) => (props.$align === 'right' ? 'right' : 'left')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  line-height: 1.2;
`;

const ScoreValue = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  min-width: 56px;
  text-align: center;
  /* Default to the card's color; W/L/D scoreColor passed inline overrides
     when present. Without this the global * { color: theme.green } repaints
     a score with no explicit scoreColor lime. */
  color: inherit;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid ${(props) => props.theme.borderColor};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// color: inherit on children defeats the global "* { color: theme.green }"
// rule that would otherwise repaint the wrapped <Moment> span lime.
const MetaItem = styled.span`
  color: inherit;
  & > * { color: inherit; }
`;

const MetaSeparator = styled.span`
  opacity: 0.5;
  color: inherit;
`;
